import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import {
  AdminFormPage,
  ConfirmDeleteModal,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useChangeOrderAccess from "../hooks/useChangeOrderAccess";
import { useChangeOrder, useChangeOrderMutations } from "../api";
import ChangeOrderForm from "../ChangeOrderForm/ChangeOrderForm";
import type { ChangeOrderFormValues } from "../types";

interface Props {
  projectId: string;
}

const FORM_ID = "change-order-form";

const Edit = ({ projectId }: Props) => {
  const navigate = useNavigate();
  const { changeOrderId = "" } = useParams();
  const access = useChangeOrderAccess();
  const { changeOrder, isLoading, error, refetch } =
    useChangeOrder(changeOrderId);
  const { update, remove } = useChangeOrderMutations();
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ChangeOrderFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) return <ScopeStackSpinner />;
  if (!changeOrder) {
    return (
      <div className="p-3">
        {error
          ? "This change order could not be loaded."
          : "Change order not found."}
      </div>
    );
  }
  // The route's project must be the change order's parent.
  if (changeOrder.parentProjectId !== projectId) {
    return <Navigate to=".." replace />;
  }

  const { label } = changeOrder;
  const canEdit = access.canEdit(changeOrder);
  const canDelete = access.canDelete(changeOrder);
  // The form reads initialValues on mount only; markSaved remounts it.
  const initialValues: ChangeOrderFormValues = savedInitial ?? {
    referenceOnly: changeOrder.referenceOnly,
    cause: changeOrder.cause,
    causeNote: changeOrder.causeNote,
    externalReference: changeOrder.externalReference,
    externalUrl: changeOrder.externalUrl,
    sourceItems: [],
  };

  const handleSubmit = async (values: ChangeOrderFormValues) => {
    setSubmitting(true);
    try {
      await update(changeOrder.id, values);
      markSaved(values, `${label} saved.`);
      // The generated API defines no cache tags; refetch so the cached GET
      // holds the server-fresh record.
      refetch();
    } catch (err) {
      console.error("Failed to update change order", err);
      setToast(saveErrorToast(err, "change order"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      await remove(changeOrder.id);
      navigate("..", {
        state: { toast: { variant: "success", text: `${label} deleted.` } },
      });
    } catch (err) {
      console.error("Failed to delete change order", err);
      setToast(saveErrorToast(err, "change order", "remove"));
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title={label}
        formId={canEdit ? FORM_ID : undefined}
        onCancel={() => navigate("..")}
        isSubmitting={submitting}
        extraActions={
          canDelete ? (
            <Button
              variant="outline-danger"
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={submitting}
            >
              Delete
            </Button>
          ) : undefined
        }
      >
        <ChangeOrderForm
          key={formKey}
          id={FORM_ID}
          mode="edit"
          projectId={projectId}
          initialValues={initialValues}
          readOnly={!canEdit}
          childProjectId={changeOrder.childProjectId}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      <ConfirmDeleteModal
        open={confirmOpen}
        title={`Delete ${label}?`}
        message={
          changeOrder.referenceOnly
            ? `This permanently deletes ${label}. Continue?`
            : `This permanently deletes ${label} and its change-order project. Continue?`
        }
        confirmLabel="Permanently Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
